import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Sliders, TrendingDown, TrendingUp, RotateCcw, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { isActiveRisk } from './shared/riskStatus';

function fmtMoney(val) {
  if (val == null) return '—';
  const n = Number(val);
  if (isNaN(n)) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

// Two-decimal formatter for P90 comparison tiles so that
// "$190.70M − $190.54M = $160K" is internally consistent.
function fmtMoneyP90(val) {
  if (val == null) return '—';
  const n = Number(val);
  if (isNaN(n)) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

function fmtPct(val) {
  if (val == null) return '';
  return `${Number(val).toFixed(1)}%`;
}

const COST_CATS = [
  { label: 'Direct Costs',       pct: 0.30, color: 'text-red-600',    badge: 'bg-red-50 border-red-200'       },
  { label: 'Bus. Interruption',  pct: 0.25, color: 'text-orange-600', badge: 'bg-orange-50 border-orange-200'  },
  { label: 'Recovery Costs',     pct: 0.20, color: 'text-blue-600',   badge: 'bg-blue-50 border-blue-200'     },
  { label: 'Regulatory & Fines', pct: 0.15, color: 'text-purple-600', badge: 'bg-purple-50 border-purple-200'  },
  { label: 'Reputation Damage',  pct: 0.10, color: 'text-pink-600',   badge: 'bg-pink-50 border-pink-200'     },
];

const SEV_STYLES = {
  CRITICAL: { background: '#fee2e2', color: '#b91c1c' },
  HIGH:     { background: '#ffedd5', color: '#c2410c' },
  MEDIUM:   { background: '#fef3c7', color: '#92400e' },
  LOW:      { background: '#f3f4f6', color: '#4b5563' },
};

// Severity resolution mirrors the parent RansomwareAssessmentRisksView risk table:
// user override first, then residual, then inherent rating.
const severityOf = (r) =>
  (r.userRating || r.residualRiskRating || r.inherentRiskRating || '').toUpperCase();

// The risk list comes from /ransomwareAssessments/{id}/risks (RiskDetailsDto), which carries
// the unified risk id as `id`; older payloads used `ransomwareRiskId`. Selection, the simulate
// call and the selected-risk figures must all use the same key.
const riskKeyOf = (r) => r.ransomwareRiskId || r.id;

// Stage inference from RansomwareRiskCategory — mirrors backend ResolveStage keyword fallback.
// Requires riskCategory to be present in the risk DTO (added to backend riskDtos).
function inferStage(risk) {
  const cat = (risk.riskCategory ?? '').toUpperCase();
  if (cat.includes('CREDENTIAL') || cat.includes('AUTH') || cat.includes('MFA')
      || cat.includes('PHISH') || cat.includes('IDENTITY') || cat.includes('ACCESS CONTROL')
      || cat.includes('PRE') || cat.includes('INITIAL') || cat.includes('RECON'))
    return 'Pre-Attack';
  if (cat.includes('BACKUP') || cat.includes('RECOVERY') || cat.includes('RESTORE')
      || cat.includes('CONTINUITY') || cat.includes('DATA PROTECT') || cat.includes('POST'))
    return 'Post-Attack';
  if (cat) return 'During Attack';
  return null; // unknown — don't guess if no category at all
}

export default function RansomwareWhatIfPanel({ risks, assessmentId, baselineP90 }) {
  const [selected,        setSelected]        = useState(new Set());
  const [result,          setResult]          = useState(null);
  const [loading,         setLoading]         = useState(false);
  const [page,            setPage]            = useState(0);
  const [severityFilter,  setSeverityFilter]  = useState('ALL');
  const [showGross,       setShowGross]       = useState(false);
  const [showBeforeAfter, setShowBeforeAfter] = useState(false);
  const [showFairInfo,    setShowFairInfo]    = useState(false);
  const [showMCInfo,      setShowMCInfo]      = useState(false);
  const debounceRef             = useRef(null);
  const apiCall                 = useApi();

  const PAGE_SIZE    = 20;
  // Only active risks are simulatable — remediated/closed risks are already "closed",
  // so a "what if these were closed?" scenario is meaningless for them.
  const allEligible  = (risks ?? []).filter(r => isActiveRisk(r) && r.finalCalculatedMonetaryImpact != null);
  const eligibleRisks = severityFilter === 'ALL'
    ? allEligible
    : allEligible.filter(r => severityOf(r) === severityFilter);
  const totalPages    = Math.ceil(eligibleRisks.length / PAGE_SIZE);
  const pagedRisks    = eligibleRisks.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const simulate = useCallback(async (closedIds) => {
    if (closedIds.size === 0) { setResult(null); return; }
    setLoading(true);
    try {
      // Never pass cachedBaselineP90 — always run baseline + scenario in the same
      // Monte Carlo call so both use the same random seed. Passing a cached P90
      // from a prior independent run causes the comparison to be meaningless:
      // simulation variance alone can make the scenario P90 appear higher than
      // baseline even when risks were removed.
      const data = await apiCall('POST', `/ransomwareWhatIf/${assessmentId}`, { closedRiskIds: Array.from(closedIds) });
      setResult(data);
    } catch {
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [apiCall, assessmentId]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => simulate(next), 400);
      return next;
    });
  };

  const reset = () => {
    setSelected(new Set());
    setResult(null);
    clearTimeout(debounceRef.current);
  };

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  if (allEligible.length < 3) return null;

  // ── Derived analytics for selected risks ────────────────────────────────────
  const selectedRisks = allEligible.filter(r => selected.has(riskKeyOf(r)));

  const sevCounts = selectedRisks.reduce((acc, r) => {
    const s = severityOf(r) || 'LOW';
    acc[s] = (acc[s] ?? 0) + 1;
    return acc;
  }, {});

  const stageCounts = { 'Pre-Attack': 0, 'During Attack': 0, 'Post-Attack': 0, unknown: 0 };
  selectedRisks.forEach(r => {
    const s = inferStage(r);
    if (s) stageCounts[s] = (stageCounts[s] ?? 0) + 1;
    else    stageCounts.unknown++;
  });
  const hasStageData = selectedRisks.some(r => r.riskCategory);

  // Gross impact = sum of FinalCalculatedMonetaryImpact of selected risks
  // (same basis as TPRM financial tab — what this risk costs if it materialises)
  const grossSelected = selectedRisks.reduce(
    (sum, r) => sum + Number(r.finalCalculatedMonetaryImpact ?? 0), 0
  );

  const reductionAmount = Number(result?.p90ReductionAmount ?? 0);
  const scenarioP90Val  = Number(result?.scenarioP90 ?? 0);
  const currentP90Val   = Number(result?.baselineP90 ?? baselineP90 ?? 0);
  const hasResult       = !loading && result != null;

  return (
    <div className="rounded-lg border border-purple-200 bg-purple-50 mb-4" style={{ padding: '16px 0' }}>

      {/* Header */}
      <div className="flex items-center justify-between" style={{ padding: '0 16px 12px 16px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <div className="flex items-center gap-2">
          <Sliders className="w-4 h-4 text-purple-600" />
          <span className="text-sm font-semibold text-gray-800">Scenario Simulator</span>
          <span className="text-xs text-gray-400">What if these risks were closed?</span>
        </div>
        {selected.size > 0 && (
          <button onClick={reset} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
        )}
      </div>

      {/* Severity filter */}
      <div style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: 500 }}>Severity:</span>
        <select
          value={severityFilter}
          onChange={e => { setSeverityFilter(e.target.value); setPage(0); }}
          style={{ fontSize: '12px', padding: '3px 8px', borderRadius: '4px', border: '1px solid #e5e7eb', background: '#fff', color: '#374151', cursor: 'pointer' }}
        >
          <option value="ALL">All ({allEligible.length})</option>
          {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(sev => {
            const count = allEligible.filter(r => severityOf(r) === sev).length;
            return <option key={sev} value={sev}>{sev.charAt(0) + sev.slice(1).toLowerCase()} ({count})</option>;
          })}
        </select>
      </div>

      {/* Risk checklist */}
      <div style={{ maxHeight: '320px', overflowY: 'auto', paddingTop: '4px' }}>
        {eligibleRisks.length === 0 && (
          <div style={{ padding: '24px 16px', textAlign: 'center', fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>
            No risks match the selected severity filter.
          </div>
        )}
        {pagedRisks.map(r => {
          const sev      = severityOf(r);
          const sevStyle = SEV_STYLES[sev] ?? SEV_STYLES.LOW;
          const title    = r.db_RiskTitle || r.riskTitle || r.ransomwareRiskTitle || '';
          return (
            <label
              key={riskKeyOf(r)}
              style={{
                display: 'grid',
                gridTemplateColumns: '24px 80px 1fr 100px',
                gap: '12px',
                alignItems: 'center',
                padding: '10px 16px',
                borderBottom: '1px solid rgba(0,0,0,0.04)',
                cursor: 'pointer',
              }}
              className="hover:bg-white transition-colors"
            >
              <input
                type="checkbox"
                checked={selected.has(riskKeyOf(r))}
                onChange={() => toggle(riskKeyOf(r))}
                className="w-3.5 h-3.5 rounded accent-purple-600"
                style={{ justifySelf: 'center' }}
              />
              <span style={{ ...sevStyle, width: '72px', textAlign: 'center', padding: '2px 0', fontSize: '11px', fontWeight: 600, borderRadius: '4px', letterSpacing: '0.3px', display: 'inline-block' }}>
                {sev || 'LOW'}
              </span>
              <span style={{ fontSize: '13px', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={title}>
                {title || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Untitled risk</span>}
              </span>
              <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '13px', color: '#374151', fontWeight: 500 }}>
                {fmtMoney(r.finalCalculatedMonetaryImpact)}
              </span>
            </label>
          );
        })}
      </div>

      {/* Pagination footer */}
      <div style={{ padding: '8px 16px 0 16px', borderTop: '1px solid rgba(0,0,0,0.06)', fontSize: '12px', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>
          {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, eligibleRisks.length)} of {eligibleRisks.length} risks
          {selected.size > 0 && <span style={{ marginLeft: 8, color: '#7c3aed', fontWeight: 600 }}>{selected.size} selected</span>}
        </span>
        {totalPages > 1 && (
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid #e5e7eb', background: page === 0 ? '#f9fafb' : '#fff', color: page === 0 ? '#d1d5db' : '#374151', cursor: page === 0 ? 'default' : 'pointer', fontSize: 12 }}
            >
              ← Prev
            </button>
            <span style={{ padding: '2px 6px', fontSize: 11, color: '#9ca3af' }}>{page + 1}/{totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page === totalPages - 1}
              style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid #e5e7eb', background: page === totalPages - 1 ? '#f9fafb' : '#fff', color: page === totalPages - 1 ? '#d1d5db' : '#374151', cursor: page === totalPages - 1 ? 'default' : 'pointer', fontSize: 12 }}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="text-xs text-gray-400 text-center py-2 mt-2">Simulating…</div>
      )}

      {hasResult && (
        <div className="mx-4 mt-3 space-y-4">

          {/* ── FAIR EAL summary (primary) ────────────────────────────────── */}
          {result.baselineFairEAL != null && (
            <div className="bg-white rounded border border-emerald-200 overflow-hidden">
              <div className="p-3">
                <div className="grid grid-cols-4 mb-2" style={{ gridTemplateRows: 'auto auto' }}>
                  <div className="text-xs text-emerald-600 font-semibold mb-0.5">Current FAIR-Aligned EAL</div>
                  <div className="text-xs text-emerald-600 font-semibold mb-0.5">Scenario FAIR-Aligned EAL</div>
                  <div className="text-xs text-emerald-600 font-semibold mb-0.5">EAL Reduction</div>
                  <div className="text-xs text-gray-400 mb-0.5">Selected Risk{result.risksRemovedCount !== 1 ? 's' : ''} Impact</div>
                  <div className="text-base font-bold text-emerald-700">{fmtMoney(result.baselineFairEAL)}</div>
                  <div className={`text-base font-bold ${Number(result.fairEALReductionAmount ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtMoney(result.scenarioFairEAL)}</div>
                  <div className={`text-base font-bold ${Number(result.fairEALReductionAmount ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'} flex items-center gap-1`}>
                    {Number(result.fairEALReductionAmount ?? 0) >= 0
                      ? <TrendingDown className="w-3.5 h-3.5 flex-shrink-0" />
                      : <TrendingUp className="w-3.5 h-3.5 flex-shrink-0" />}
                    {fmtMoney(result.fairEALReductionAmount)}
                    <span className="text-xs font-normal text-gray-400 ml-1">({fmtPct(result.fairEALReductionPercent)})</span>
                  </div>
                  <div className="text-base font-bold text-gray-700">{grossSelected > 0 ? fmtMoney(grossSelected) : '—'}</div>
                </div>
                <button
                  onClick={() => setShowFairInfo(v => !v)}
                  className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-800 transition-colors mt-1"
                >
                  <HelpCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  How is FAIR-Aligned EAL calculated?
                  {showFairInfo ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              </div>
              {showFairInfo && (
                <div className="border-t border-emerald-100 bg-emerald-50 px-4 py-3 space-y-2">
                  <p className="text-xs font-semibold text-emerald-800">What is FAIR-Aligned EAL?</p>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    <strong>FAIR</strong> (Factor Analysis of Information Risk) is an internationally recognised risk quantification standard.
                    <strong> EAL (Expected Annual Loss)</strong> is its core output — the average dollar loss you expect per year across your entire risk portfolio.
                  </p>
                  <p className="text-xs font-semibold text-emerald-800 mt-2">How it is calculated</p>
                  <div className="text-xs text-gray-600 leading-relaxed space-y-1">
                    <p>For each risk:</p>
                    <p className="ml-3 font-mono bg-white border border-emerald-100 rounded px-2 py-1 text-emerald-700">
                      EAL = Single Loss Expectancy (SLE) × Annual Probability
                    </p>
                    <p className="ml-3">• <strong>SLE</strong> — the estimated financial impact if this specific risk materialises once.</p>
                    <p className="ml-3">• <strong>Annual Probability</strong> — the likelihood it materialises in any given year (e.g. 15% = once every ~7 years).</p>
                    <p>Portfolio EAL = sum of all individual risk EALs.</p>
                  </div>
                  <p className="text-xs font-semibold text-emerald-800 mt-2">What it tells you</p>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    EAL is your <strong>average annual budget exposure</strong> — analogous to an insurance premium. It answers: <em>"On average, how much should we expect to lose per year?"</em> It is a planning figure, not a worst-case figure.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── P90 summary (supporting detail) ──────────────────────────── */}
          <div className="bg-white rounded border border-purple-100 overflow-hidden">
            <div className="p-3">
              <div className="grid grid-cols-4 mb-2" style={{ gridTemplateRows: 'auto auto' }}>
                <div className="text-xs text-gray-400 mb-0.5">Current P90</div>
                <div className="text-xs text-gray-400 mb-0.5">Scenario P90</div>
                <div className="text-xs text-gray-400 mb-0.5">P90 Reduction</div>
                <div className="text-xs text-gray-400 mb-0.5">Selected Risk{result.risksRemovedCount !== 1 ? 's' : ''} Impact</div>
                <div className="text-base font-bold text-gray-700">{fmtMoneyP90(result.baselineP90)}</div>
                <div className={`text-base font-bold ${reductionAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtMoneyP90(result.scenarioP90)}</div>
                <div className={`text-base font-bold ${reductionAmount >= 0 ? 'text-green-600' : 'text-red-600'} flex items-center gap-1`}>
                  {reductionAmount >= 0
                    ? <TrendingDown className="w-3.5 h-3.5 flex-shrink-0" />
                    : <TrendingUp className="w-3.5 h-3.5 flex-shrink-0" />}
                  {fmtMoneyP90(reductionAmount)}
                  <span className="text-xs font-normal text-gray-400 ml-1">({fmtPct(result.p90ReductionPercent)})</span>
                </div>
                <div className="text-base font-bold text-gray-700">{grossSelected > 0 ? fmtMoney(grossSelected) : '—'}</div>
              </div>
              <div className="text-xs text-gray-400 mt-1.5 mb-2">
                {result.risksRemovedCount} risk{result.risksRemovedCount !== 1 ? 's' : ''} hypothetically
                closed · {result.risksRemainingCount} remaining in simulation
              </div>
              <button
                onClick={() => setShowMCInfo(v => !v)}
                className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 transition-colors"
              >
                <HelpCircle className="w-3.5 h-3.5 flex-shrink-0" />
                How is Monte Carlo P90 calculated? Why is it higher than FAIR-Aligned EAL?
                {showMCInfo ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            </div>
            {showMCInfo && (
              <div className="border-t border-purple-100 bg-purple-50 px-4 py-3 space-y-2">
                <p className="text-xs font-semibold text-purple-800">What is Monte Carlo P90?</p>
                <p className="text-xs text-gray-600 leading-relaxed">
                  <strong>Monte Carlo simulation</strong> runs 10,000 simulated years of your risk portfolio. In each simulated year, every risk independently either fires or doesn't (based on its annual probability), and if it fires, a random loss amount is drawn. The <strong>P90</strong> is the 90th-percentile outcome — meaning 90% of simulated years produce a loss below this figure.
                </p>
                <p className="text-xs font-semibold text-purple-800 mt-2">How it is calculated</p>
                <div className="text-xs text-gray-600 leading-relaxed space-y-1">
                  <p>Each of the 10,000 iterations:</p>
                  <p className="ml-3">1. For every risk, flip a weighted coin (Bernoulli trial at annual probability).</p>
                  <p className="ml-3">2. If the risk fires, draw a random loss from its triangular distribution (min → most-likely → max SLE).</p>
                  <p className="ml-3">3. Sum all losses that fired — that is the portfolio loss for that simulated year.</p>
                  <p>Sort all 10,000 annual losses. P90 = the value at the 9,000th position.</p>
                </div>
                <p className="text-xs font-semibold text-purple-800 mt-2">Why is P90 higher than the EAL estimate?</p>
                <div className="text-xs text-gray-600 leading-relaxed space-y-1">
                  <p>They measure <strong>different things</strong>:</p>
                  <p className="ml-3">• <strong>EAL (Expected Annual Loss)</strong> = your <em>average</em> annual loss — what you expect in a typical year.</p>
                  <p className="ml-3">• <strong>MC P90</strong> = your <em>bad year</em> loss — what you face in the worst 1 in 10 years.</p>
                  <p>Losses don't occur smoothly every year. In most years very few risks fire; in a bad year many fire at once. P90 captures that clustering effect. A P90 that is 4–7× EAL is <strong>normal and expected</strong> for a ransomware portfolio — it reflects real-world incident clustering, not a calculation error.</p>
                  <p className="mt-1 p-2 bg-white border border-purple-100 rounded text-purple-700 font-medium">
                    Think of it this way: EAL is your insurance premium. P90 is the maximum claim you should be prepared to cover.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ── Gross financial impact by cost category ──────── */}
          {grossSelected > 0 && (
            <div className="bg-white rounded border border-emerald-200 overflow-hidden">
              <button
                onClick={() => setShowGross(v => !v)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-emerald-50 hover:bg-emerald-100 transition-colors text-left"
              >
                <span className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5">
                  💰 Gross Financial Impact by Cost Category
                </span>
                {showGross ? <ChevronUp className="w-4 h-4 text-emerald-600 shrink-0" /> : <ChevronDown className="w-4 h-4 text-emerald-600 shrink-0" />}
              </button>
              {showGross && (
                <div className="p-4">
                  <p className="text-xs text-gray-400 mb-3">
                    Total gross impact of {result.risksRemovedCount} selected risk{result.risksRemovedCount !== 1 ? 's' : ''}: <span className="font-semibold text-gray-600">{fmtMoney(grossSelected)}</span>
                    {' '}— what these risks cost if they materialise. Portfolio P90 reduction: <span className="font-semibold text-green-700">{fmtMoney(reductionAmount)}</span>.
                  </p>
                  <div className="grid grid-cols-5 gap-2">
                    {COST_CATS.map(({ label, pct, color, badge }) => (
                      <div key={label} className={`text-center p-2 rounded border ${badge}`}>
                        <div className="text-xs font-semibold text-gray-500 mb-1 leading-tight">{label}</div>
                        <div className={`text-sm font-bold ${color} leading-tight`}>{fmtMoney(grossSelected * pct)}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{Math.round(pct * 100)}%</div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Allocation based on standard cyber-loss taxonomy (CISA / Ponemon). Actual split varies by incident type.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── Before / After portfolio exposure by cost category ───────────── */}
          <div className="bg-white rounded border border-gray-200 overflow-hidden">
            <button
              onClick={() => setShowBeforeAfter(v => !v)}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
              <span className="text-sm font-semibold text-gray-800">
                📊 Before / After Portfolio Exposure by Category
              </span>
              {showBeforeAfter ? <ChevronUp className="w-4 h-4 text-gray-500 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" />}
            </button>
            {showBeforeAfter && (
              <div className="p-4">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-400 border-b border-gray-100">
                      <th className="text-left py-1.5 font-medium">Category</th>
                      <th className="text-right py-1.5 font-medium">Current P90</th>
                      <th className="text-right py-1.5 font-medium">Scenario P90</th>
                      <th className="text-right py-1.5 font-medium">P90 Saved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COST_CATS.map(({ label, pct, color }) => {
                      const curr  = currentP90Val * pct;
                      const scen  = scenarioP90Val * pct;
                      const saved = curr - scen;
                      return (
                        <tr key={label} className="border-b border-gray-50">
                          <td className="py-1.5 text-gray-600">{label}</td>
                          <td className="py-1.5 text-right tabular-nums text-gray-700">{fmtMoneyP90(curr)}</td>
                          <td className={`py-1.5 text-right tabular-nums font-medium ${color}`}>{fmtMoneyP90(scen)}</td>
                          <td className="py-1.5 text-right tabular-nums font-semibold text-green-600">↓ {fmtMoneyP90(saved)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Selected risk profile ────────────────────────────────────────── */}
          <div className="bg-white rounded border border-gray-200 p-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-3">
              🎯 Selected Risk Profile
              <span className="text-xs font-normal text-gray-400 ml-2">
                {selectedRisks.length} risk{selectedRisks.length !== 1 ? 's' : ''}
              </span>
            </h4>
            <div className={`grid gap-6 ${hasStageData ? 'grid-cols-2' : 'grid-cols-1'}`}>

              {/* Severity distribution */}
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Severity Mix</div>
                <div className="space-y-1.5">
                  {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(s => {
                    const count = sevCounts[s] ?? 0;
                    if (count === 0) return null;
                    const style = SEV_STYLES[s] ?? SEV_STYLES.LOW;
                    return (
                      <div key={s} className="flex items-center gap-2">
                        <span style={{ ...style, minWidth: 60, textAlign: 'center', padding: '1px 4px', fontSize: '10px', fontWeight: 700, borderRadius: 3, display: 'inline-block' }}>
                          {s}
                        </span>
                        <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                          <div className="h-1.5 rounded-full"
                            style={{ width: `${(count / selectedRisks.length) * 100}%`, background: style.color }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 tabular-nums w-4">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Kill-chain stage coverage — only shown when riskCategory data is available */}
              {hasStageData && (
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Kill-Chain Coverage</div>
                  <div className="space-y-1.5">
                    {[
                      { stage: 'Pre-Attack',    color: '#3b82f6' },
                      { stage: 'During Attack', color: '#f97316' },
                      { stage: 'Post-Attack',   color: '#ef4444' },
                    ].map(({ stage, color }) => {
                      const count = stageCounts[stage] ?? 0;
                      return (
                        <div key={stage} className="flex items-center gap-2">
                          <span className="text-xs text-gray-600 w-24 shrink-0">{stage}</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                            <div className="h-1.5 rounded-full transition-all"
                              style={{ width: selectedRisks.length > 0 ? `${(count / selectedRisks.length) * 100}%` : '0%', background: color, opacity: count > 0 ? 1 : 0.25 }}
                            />
                          </div>
                          <span className="text-xs text-gray-500 tabular-nums w-4">{count}</span>
                        </div>
                      );
                    })}
                    {stageCounts.unknown > 0 && (
                      <p className="text-xs text-gray-400 italic mt-1">
                        {stageCounts.unknown} risk{stageCounts.unknown !== 1 ? 's' : ''} without category — stage not inferred.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      )}

      {/* Disclaimer */}
      <p className="text-xs text-gray-400" style={{ padding: '8px 16px 0 16px' }}>
        This scenario does not modify any data. Cost category allocations are illustrative estimates. Results are hypothetical projections only.
      </p>
    </div>
  );
}
